// lib/slides/elementLayout.ts
import type { SlideElement } from '#types/types';
import { measureStandardText, standardDisplayText, standardFontFor } from './standardText';
import { layoutLineLimit, wrapStampText } from './stampLayout';
import { normFrame } from './resizeFrame';
import { ELEMENT_VERDICT_COLORS } from './validationPoints';
import { buildBlockArtwork } from './blockArtwork';

export const ELEMENT_OVERFLOW_MESSAGE = 'This text does not fit. Shorten it, reduce its font size or enlarge its frame.';
export const slideColor = (input: string | undefined, fallback = '#FF3B30'): string => {
  const hex = input?.trim();
  if (hex && /^#[\da-f]{6}$/i.test(hex)) return hex;
  if (hex && /^#[\da-f]{3}$/i.test(hex)) return '#' + [...hex.slice(1)].map(c => c + c).join('');
  return fallback;
};
const positive = (value: number | undefined, fallback: number) => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

export function buildElementLayout(element: Exclude<SlideElement, { type: 'note' }>) {
  const frame = normFrame(element.frame);
  const { w, h } = frame;
  const opacity = Math.max(0, Math.min(1, element.opacity ?? 1));
  const rotation = element.rotation ?? 0;
  const color = slideColor(element.color, element.type === 'text' ? '#111111' : element.type === 'patch' ? '#111827' : '#FF3B30');
  const strokeWidth = element.type === 'rect' || element.type === 'ellipse' || element.type === 'arrow'
    ? Math.max(0, element.strokeWidth ?? 2) : element.type === 'stamp' ? 2 : 0;
  let border: string | undefined;
  let fill: string | undefined;
  let fillOpacity = opacity;
  let path = '';
  let size = 12;
  let font = standardFontFor();
  let textColor = color;
  let lineStep = 1.25;
  let align: 'left' | 'center' | 'right' = 'center';
  let topAligned = false;
  let box = { x: 4, y: 4, w: w - 8, h: h - 8 };
  const roundedRect = (radius: number) => {
    const r = Math.max(0, Math.min(radius, w / 2, h / 2));
    if (r === 0) return `M 0 0 H ${w} V ${h} H 0 Z`;
    return `M ${r} 0 H ${w - r} A ${r} ${r} 0 0 1 ${w} ${r} V ${h - r} A ${r} ${r} 0 0 1 ${w - r} ${h} H ${r} A ${r} ${r} 0 0 1 0 ${h - r} V ${r} A ${r} ${r} 0 0 1 ${r} 0 Z`;
  };
  switch (element.type) {
    case 'text':
      size = positive(element.fontSize, 16);
      font = standardFontFor(element.fontFamily, element.fontWeight === 'bold', element.italic === true);
      align = element.align ?? 'left'; lineStep = positive(element.lineHeight, 1.25);
      box = { x: 0, y: 0, w, h }; topAligned = true;
      break;
    case 'rect':
    case 'ellipse': {
      border = color;
      if (element.fill) { fill = color; fillOpacity = 0.1 * opacity; }
      const inset = strokeWidth / 2 + 4;
      const iw = element.type === 'ellipse' ? Math.max(0, w - strokeWidth) / Math.SQRT2 : w - strokeWidth;
      const ih = element.type === 'ellipse' ? Math.max(0, h - strokeWidth) / Math.SQRT2 : h - strokeWidth;
      box = element.type === 'ellipse'
        ? { x: (w - iw) / 2 + 4, y: (h - ih) / 2 + 4, w: iw - 8, h: ih - 8 }
        : { x: inset, y: inset, w: w - 2 * inset, h: h - 2 * inset };
      path = element.type === 'ellipse' && w > 0 && h > 0
        ? `M 0 ${h / 2} A ${w / 2} ${h / 2} 0 1 0 ${w} ${h / 2} A ${w / 2} ${h / 2} 0 1 0 0 ${h / 2} Z`
        : roundedRect(2);
      break;
    }
    case 'arrow': {
      border = color; size = 9; align = 'left'; topAligned = true;
      const sx = element.frame.x - frame.x, sy = element.frame.y - frame.y;
      const ex = sx + element.frame.w, ey = sy + element.frame.h;
      const angle = Math.atan2(sy - ey, sx - ex), barb = Math.max(8, strokeWidth * 4);
      path = `M ${sx} ${sy} L ${ex} ${ey}`;
      for (const offset of [-Math.PI / 6, Math.PI / 6]) path += ` M ${ex} ${ey} L ${ex + barb * Math.cos(angle + offset)} ${ey + barb * Math.sin(angle + offset)}`;
      box = { x: 0, y: 0, w: Math.max(w, 80), h: Infinity };
      break;
    }
    case 'patch':
      fill = slideColor(element.background, '#FFFFFF'); size = positive(element.fontSize, 14);
      path = `M 0 0 H ${w} V ${h} H 0 Z`;
      break;
    case 'stamp':
      border = ELEMENT_VERDICT_COLORS[element.verdict ?? 'fail']; fill = border; textColor = border;
      fillOpacity = 0.15 * opacity; size = Math.min(h / 2, w * 0.18); font = standardFontFor('sans', true);
      path = roundedRect(6);
      break;
    case 'image':
    case 'block':
      break;
  }
  const raw = 'text' in element ? element.text ?? '' : '';
  const display = standardDisplayText(raw, font);
  const measure = (text: string, fs: number) => measureStandardText(text, fs, font);
  const lineHeight = size * lineStep;
  const lineLimit = layoutLineLimit(box.h, lineHeight);
  const lines = raw ? wrapStampText(display, size, false, Math.max(0, box.w), measure, lineLimit) : [];
  // Tight leading may overlap lines, but cannot move the last baseline outside
  // the frame unnoticed. Retain one em plus descender room for the final line.
  const textHeight = lines.length ? Math.max(lineHeight, size * 1.25) + (lines.length - 1) * lineHeight : 0;
  if (element.type === 'arrow') box = { ...box, y: -4 - textHeight, h: textHeight };
  const overflow = element.type === 'block' ? buildBlockArtwork(element.block, element.fields, w, h).overflow : Boolean(raw) && (lines.length >= lineLimit || size <= 0 || box.w <= 0 || textHeight > box.h + 1e-7 || lines.some(line => measure(line, size) > box.w + 1e-7));
  const textTop = box.y + (topAligned ? 0 : Math.max(0, (box.h - textHeight) / 2));
  const texts = lines.map((text, index) => {
    const width = measure(text, size);
    const slack = Math.max(0, box.w - width);
    return { text, x: box.x + (align === 'center' ? slack / 2 : align === 'right' ? slack : 0), y: textTop + size + index * lineHeight, width };
  });
  return { frame, rotation, opacity, color, border, strokeWidth, fill, fillOpacity, path, texts, textColor, size, font, lineHeight, align, box, overflow };
}
